import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useSelector } from 'react-redux';
import CommonLoader from '../CommonLoader';
import { useTranslation } from 'react-i18next';
import { useMasterLabel } from '../../hooks/useMasterLabel';

// Per-row translated specialty label for the earnings breakdown. Chart data carries
// the specialty's `specialtyId` alongside the snapshot `name`, so the cache resolves
// cleanly and the snapshot is the fallback when no translation exists yet.
const SpecialtyLegendLabel = ({ specialty }) => {
  const label = useMasterLabel('masterSpecialty', specialty?.specialtyId, 'name', specialty?.name || '');
  return <p className="text-xs sm:text-sm font-semibold text-gray-700 capitalize">{label}</p>;
};

const EarningsAnalytics = ({ dateRange, selectedPeriod, customStartDate, customEndDate, specialtyId, addressTypeName, earningsAnalytics, loading, embedded }) => {
  const { t } = useTranslation('dashboard');
  const { selectedDoctor } = useSelector((state) => state.doctor);

  const earningsLoading = loading;

  // Get currency symbol based on timezone
  const getCurrencySymbol = () => {
    const timeZone = selectedDoctor?.timeZone?.toLowerCase();
    return timeZone === 'asia/kolkata' || timeZone === 'asia/kolkattta' ? '₹' : '€';
  };

  // Get earnings data from API or use default
  const getEarningsData = () => {
    if (earningsAnalytics?.data) {
      const specialties = earningsAnalytics.data.map(item => ({
        specialtyId: item.specialtyId,
        name: item.specialtyName,
        amount: item.totalEarnings,
        count: item.appointmentCount,
        color: item.color,
      }));

      return {
        total: earningsAnalytics.totalEarnings || 0,
        totalAppointments: earningsAnalytics.totalAppointments || 0,
        specialties: specialties,
      };
    }

    return {
      total: 0,
      totalAppointments: 0,
      specialties: [],
    };
  };

  const currentEarningsData = getEarningsData();

  const chartData = currentEarningsData.specialties.map((spec) => ({
    name: spec.name,
    value: spec.amount,
    color: spec.color,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = currentEarningsData.total;
      const percentage = total ? ((data.value / total) * 100).toFixed(2) : 0;
      const currencySymbol = getCurrencySymbol();
      return (
        <div data-testid="earnings-analytics-tooltip" className="bg-white p-2 border border-gray-200 rounded shadow-lg">
          <p data-testid="earnings-analytics-tooltip-name" className="font-semibold text-gray-900">{data.name}</p>
          <p data-testid="earnings-analytics-tooltip-amount" className="text-sm text-gray-700">{currencySymbol} {data.value.toLocaleString()}</p>
          <p data-testid="earnings-analytics-tooltip-percent" className="text-xs text-gray-500">({percentage}%)</p>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      data-testid="earnings-analytics"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className={embedded ? "w-full" : "bg-white w-full rounded-xl p-4 border border-gray-200/50 shadow-md"}
    >
      <div data-testid="earnings-analytics-header" className={embedded ? "hidden" : "flex flex-col items-start gap-3 mb-4"}>
        <h3 data-testid="earnings-analytics-title" className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
          <span data-testid="currency-icon" className="text-lg sm:text-xl mr-2 text-[#4ABDBD] font-bold">
            {getCurrencySymbol()}
          </span>
          {t('dashboard.earnings_analytics')}
        </h3>
      </div>
      <AnimatePresence mode="wait">
        {earningsLoading ? (
          <div data-testid="earnings-analytics-loading" className="flex flex-col items-center justify-center py-12">
            <CommonLoader />
            <span data-testid="earnings-analytics-loading-text" className="text-sm text-gray-600 mt-2">{t('dashboard.loading_earnings')}</span>
          </div>
        ) : (
          <motion.div
            key={selectedPeriod}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col xl:flex-row xl:gap-4"
          >
            <div data-testid="earnings-analytics-legend-side" className="space-y-4 xl:w-1/2">
              {currentEarningsData.specialties.length > 0 ? (
                <div data-testid="earnings-analytics-specialty-list" className="space-y-2 max-h-52 overflow-y-auto pr-1 custom-scrollbar">
                  {currentEarningsData.specialties.map((specialty, specIndex) => (
                    <motion.div
                      key={specialty.name}
                      data-testid={`earnings-analytics-specialty-row-${specialty.specialtyId || specialty.name}`}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: specIndex * 0.05 }}
                      className="flex items-center justify-between p-1.5"
                    >
                      <div className="flex items-center space-x-2">
                        <div
                          data-testid={`earnings-analytics-specialty-color-${specialty.specialtyId || specialty.name}`}
                          className="w-3 h-3 sm:w-4 sm:h-4 rounded-full shadow-sm"
                          style={{ backgroundColor: specialty.color }}
                        ></div>
                        <SpecialtyLegendLabel specialty={specialty} />
                      </div>
                      <div className="flex items-center space-x-2">
                        <p data-testid={`earnings-analytics-specialty-amount-${specialty.specialtyId || specialty.name}`} className="text-xs sm:text-sm font-bold text-gray-900">
                          {getCurrencySymbol()} {specialty.amount.toLocaleString()}
                        </p>
                        {/* <span className="text-xs text-gray-500">({specialty.count})</span> */}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div data-testid="earnings-analytics-empty-state" className="flex flex-col items-center justify-center py-8">
                  <span data-testid="earnings-analytics-empty-currency" className="text-5xl text-gray-300 mb-2 font-bold">{getCurrencySymbol()}</span>
                  <p data-testid="earnings-analytics-empty-text" className="text-sm text-gray-600">{t('dashboard.no_earnings_data')}</p>
                </div>
              )}
            </div>
            <div data-testid="earnings-analytics-chart-side" className="xl:w-1/2">
              <div className="">
                <div data-testid="earnings-analytics-chart-container" className="h-48 sm:h-52 md:h-52 relative">
                  {currentEarningsData.specialties.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius="40%"
                            outerRadius="70%"
                            dataKey="value"
                            label={false}
                          >
                            {chartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div data-testid="earnings-analytics-total-overlay" className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <p data-testid="earnings-analytics-total-value" className="text-sm font-bold text-gray-900">
                          {getCurrencySymbol()} {currentEarningsData.total.toLocaleString()}
                        </p>
                        <p data-testid="earnings-analytics-total-label" className="text-xs font-medium text-gray-500">
                          {t('dashboard.total')}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div data-testid="earnings-analytics-no-chart" className="h-full flex items-center justify-center">
                      <p data-testid="earnings-analytics-no-chart-text" className="text-sm text-gray-400">{t('dashboard.no_chart_data')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EarningsAnalytics;
